import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import { TCPOffsetOption } from '@sb/motion-planning/TCPOffsetOption';

import { SpeedProfile } from '../../speed-profile';

const OrientationJitter = zod.object({
  rollRange: zod.number().nonnegative(),
  pitchRange: zod.number().nonnegative(),
  yawRange: zod.number().nonnegative(),
});

const AxisLocks = zod.object({
  roll: zod.boolean(),
  pitch: zod.boolean(),
  yaw: zod.boolean(),
});

// One cylinder of the per-level stack: an absolute radius (m) and a relative
// height (unitless weight, normalised across the stack to fill approachLength).
const PerLevel = zod.object({
  radius: zod.number().nonnegative(),
  height: zod.number().positive(),
});

// A pose source is either an inline CartesianPose (single-position case) or a
// reference to a space item whose first position is read at play time. The
// reference form is what makes Space edits propagate to a step that has
// already been configured — see Step.ts#resolvePose.
const PoseSource = zod.union([
  CartesianPose,
  zod.object({
    positionListID: zod.string(),
  }),
]);

export type PoseSource = zod.infer<typeof PoseSource>;

export default zod
  .object({
    targetPose: PoseSource,
    startingPose: PoseSource,

    bufferLength: zod.number().nonnegative(),

    // Linear (min/max) mode: radius is interpolated narrow -> wide.
    narrowRadius: zod.number().nonnegative(),
    wideRadius: zod.number().nonnegative(),

    // Per-level mode: each level's radius + relative height, stacked from the
    // target. Empty / ignored in linear mode.
    radiusMode: zod.enum(['linear', 'perLevel']).default('linear'),
    levels: zod.array(PerLevel).default([]),

    // Height sampling: 'discrete' snaps points onto levelCount planes (per
    // radius mode); 'continuous' draws each point's height from a Gaussian
    // centred on the target and uses levelCount only as the number of execution
    // batches. Orthogonal to radiusMode.
    heightMode: zod.enum(['discrete', 'continuous']).default('discrete'),

    // Continuous mode only: Gaussian height std-dev as a fraction of the
    // approach length (sigma = heightSpread * approachLength). Smaller =
    // tighter concentration near the target.
    heightSpread: zod.number().positive().default(0.5),

    sampleCount: zod.number().int().min(1),
    levelCount: zod.number().int().min(2),
    targetEmphasis: zod.number().min(0).max(1),
    randomSeed: zod.number().int().nullable(),

    orientationJitter: OrientationJitter,
    lockedAxes: AxisLocks,

    // Which tooltip the targetPose / startingPose are interpreted at. No
    // 'auto' — operators must choose explicitly. Defaults to the wrist flange.
    tcpOption: TCPOffsetOption.default('wrist'),

    // Sweep motion type. The initial move to the target is always a joint
    // move regardless of this value.
    motionKind: zod.enum(['joint', 'line']).default('joint'),

    // Default 5 mm — keep small. The final exact-target waypoint always
    // uses blendRadius=0 regardless of this value.
    blendRadius: zod.number().nonnegative().default(0.005),
    speedProfile: SpeedProfile,
  })
  .refine((a) => a.radiusMode !== 'linear' || a.wideRadius >= a.narrowRadius, {
    message: 'wideRadius must be >= narrowRadius',
    path: ['wideRadius'],
  })
  .refine((a) => a.radiusMode !== 'perLevel' || a.levels.length >= 1, {
    message: 'per-level mode requires at least one level',
    path: ['levels'],
  });
